import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  Container,
  Fade,
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
  GridRowParams,
  GridValueGetterParams,
} from '@mui/x-data-grid'
import {
  ConfirmPopup,
  DataGridStyles,
  Icon,
  Modal,
  SnackBarContext,
} from 'components'
import { DatagridPlaceholder } from 'components/datagridPlaceholder'
import { theme } from 'config'
import { format } from 'date-fns'
import { inject, observer } from 'mobx-react'
import { Course, User } from 'models'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getRoutePath, Pages } from 'routes'
import { AuthStore, RoutingStore } from 'stores'
import styled from 'styled-components'
import { DataStatus, StepType } from 'types'

function a11yProps(index: number) {
  return {
    id: `course-status-${index}`,
    'aria-controls': `course-statuspanel-${index}`,
  }
}

export interface ICourseListEditorProps {
  authStore?:    AuthStore,
  routingStore?: RoutingStore,
}

export const CourseListEditor = inject('routingStore')(observer((props: ICourseListEditorProps) => {
  const { t } = useTranslation();
  const { routingStore } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [courses,  setCourses]  = useState<Course[]>([]);
  const [users,    setUsers]    = useState<{[id: number]: User}>({});
  const [course,   setCourse]   = useState<Course>();
  const [athenId,  setAthenId]  = useState<string>('');
  const [draftCourses,     setDraftCourses]     = useState<Course[]>([]);
  const [archivedCourses,  setArchivedCourses]  = useState<Course[]>([]);
  const [publishedCourses, setPublishedCourses] = useState<Course[]>([]);
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [currentTab,       setCurrentTab]       = useState<number>(0);
  const { toast } = React.useContext(SnackBarContext);

  useEffect(() => {
    Course.get().then((c: Course[]) => setCourses(c));
  }, []);

  useEffect(() => {
    if (courses) {
      setDraftCourses(courses.filter((c) => c.status === DataStatus.Draft));
      setPublishedCourses(
        courses.filter((c) => c.status == DataStatus.Published)
      );
      setArchivedCourses(
        courses.filter((c) => c.status == DataStatus.Archived)
      );

      const created_by: number[] = courses.map(c => c.created_by);
      const updated_by: number[] = courses.map(c => c.updated_by);
      const user_ids: number[] = [...new Set([...created_by, ...updated_by].filter(Boolean))];
      User.find(user_ids).then(u => setUsers(u.reduce((res: {[id: number]: User}, val: User) => {
        res[val.id] = val;
        return res;
      }, {})));
    } else {
      setDraftCourses([]);
      setPublishedCourses([]);
      setArchivedCourses([]);
      setUsers({})
    }
  }, [courses]);

  const renderDetailsButton = (params: GridRenderCellParams<RowData>) => {
    return (
      <IconButton
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget)
          setCourse(params.row.course)
        }}
      >
        <MoreVertIcon htmlColor={theme.palette.black.main} />
      </IconButton>
    );
  }

  const columns: GridColDef[] = [
    {
      field:           'headerName',
      headerName:      t('name'),
      headerClassName: 'columnHeader',
      flex:            2,
      minWidth:        60,
      valueGetter:     (params: GridValueGetterParams) =>
        `${params.row.headerName || ''}`,
    },{
      field:           'category',
      headerName:      t('category'),
      flex:            1,
      headerClassName: 'columnHeader',
    },/* ,{
      field:           'athenId',
      headerName:      t('athen_id'),
      sortable:        false,
      flex:            1,
      headerClassName: 'columnHeader',
    }, */
    {
      field:             'createdBy',
      headerName:        t('created_by'),
      flex:              1,
      disableColumnMenu: true,
      headerClassName:   'columnHeader',
      sortable:          true,
    },{
      field:             'updatedBy',
      headerName:        t('updated_by'),
      flex:              1,
      disableColumnMenu: true,
      headerClassName:   'columnHeader',
      sortable:          true,
    },{
      field:             'latestEdit',
      headerName:        t('latest_edit'),
      type:              'dateTime',
      disableColumnMenu: true,
      flex:              1,
      headerClassName:   'columnHeader',
    },{
      field:           'actions',
      type:            'actions',
      headerName:      '',
      sortable:        false,
      flex:            1,
      maxWidth:        60,
      renderCell:      renderDetailsButton,
      align:           'right',
      headerClassName: 'actionHeader',
    },
  ];

  type RowData = {
    id:         number,
    course:     Course,
    headerName: string,
    latestEdit: string,
    category:   string,
    chapters:   number,
    athenId:    number,
    createdBy:  string,
    updatedBy:  string
  }

  let draftRows: GridRowModel<RowData>[] = [];
  draftRows = draftCourses?.map((c) => ({
    id:         c.id,
    course:     c,
    headerName: c.title,
    category:   c.category,
    chapters:   c.getChapters.length,
    athenId:    6,
    latestEdit: format(c.updated_at, 'MM/dd/yyyy HH:mm'),
    createdBy:  users[c.created_by]?.getFullName,
    updatedBy:  users[c.updated_by]?.getFullName
  }));

  let publishedRows: GridRowModel<RowData>[] = []
  publishedRows = publishedCourses?.map((c) => ({
    id:         c.id,
    course:     c,
    headerName: c.title,
    category:   c.category,
    chapters:   c.getChapters.length,
    athenId:    6,
    latestEdit: format(c.updated_at, 'MM/dd/yyyy HH:mm'),
    createdBy:  users[c.created_by]?.getFullName,
    updatedBy:  users[c.updated_by]?.getFullName
  }));

  let archivedRows: GridRowModel<RowData>[] = []
  archivedRows = archivedCourses?.map((c) => ({
    id:         c.id,
    course:     c,
    headerName: c.title,
    category:   c.category,
    chapters:   c.getChapters.length,
    athenId:    6,
    latestEdit: format(c.updated_at, 'MM/dd/yyyy HH:mm'),
    createdBy:  users[c.created_by]?.getFullName,
    updatedBy:  users[c.updated_by]?.getFullName
  }));

  return (
    <>
      <Container maxWidth='xl'>
        <Grid pt={3} container>
          <Grid
            item
            xs             = {12}
            display        = 'flex'
            justifyContent = 'space-between'
            alignItems     = 'center'
            mb             = {3}
          >
            <Typography
              variant    = 'h2'
              fontWeight = {theme.typography.fontWeightBold}
            >
              {t('courses')}
            </Typography>
            <Button
              variant   = 'contained'
              startIcon = {<Icon iconId='add' />}
              onClick   = {() => setOpenConfirmModal(true)}
            >
              {t('create_new_course')}
            </Button>
          </Grid>
          <Grid xs={12} mb={2} item>
            <Tabs
              value      = {currentTab}
              onChange   = {(_, newValue: number) => setCurrentTab(newValue)}
              aria-label = 'Course status'
            >
              <Tab label={`${t('draft')} (${draftRows.length})`} {...a11yProps(0)} />
              <Tab label={`${t('published')} (${publishedRows.length})`} {...a11yProps(1)} />
              <Tab label={`${t('archived')} (${archivedRows.length})`} {...a11yProps(2)} />
            </Tabs>
          </Grid>
        </Grid>
        <DataGridStyles>
          {currentTab === 0 && (
            <DataGrid
              autoHeight
              disableSelectionOnClick
              disableColumnSelector
              components = {{
                NoRowsOverlay: () =>
                (<DatagridPlaceholder title={t('datagrid_placeholder_draft_courses')}/>)
              }}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'headerName', sort: 'asc' }],
                },
              }}
              rowHeight          = {66}
              density            = 'standard'
              rows               = {draftRows}
              columns            = {columns}
              pageSize           = {10}
              rowsPerPageOptions = {[10]}
              onRowClick         = {(params: GridRowParams<RowData>) =>
                routingStore.push(Pages.Editor, { id: '' + params.row.id })
              }
            />
          )}
          {currentTab === 1 && (
            <DataGrid
              autoHeight
              disableSelectionOnClick
              disableColumnSelector
              components = {{
                NoRowsOverlay: () =>
                (<DatagridPlaceholder title={t('datagrid_placeholder_published_courses')}/>)
              }}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'headerName', sort: 'asc' }],
                },
              }}
              rowHeight          = {66}
              density            = 'standard'
              rows               = {publishedRows}
              columns            = {columns}
              pageSize           = {10}
              rowsPerPageOptions = {[10]}
              onRowClick         = {(params: GridRowParams<RowData>) =>
                routingStore.push(Pages.Editor, { id: '' + params.row.id })
              }
            />
          )}
          {currentTab === 2 && (
            <DataGrid
              autoHeight
              disableSelectionOnClick
              disableColumnSelector
              components = {{
                NoRowsOverlay: () =>
                (<DatagridPlaceholder title={t('datagrid_placeholder_archived_courses')}/>)
              }}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'headerName', sort: 'asc' }],
                },
              }}
              rowHeight          = {66}
              density            = 'standard'
              rows               = {archivedRows}
              columns            = {columns}
              pageSize           = {10}
              rowsPerPageOptions = {[10]}
              hideFooterPagination   = {Object.values(archivedRows).length < 10}
              onRowClick         = {(params: GridRowParams<RowData>) =>
                routingStore.push(Pages.Editor, { id: '' + params.row.id })
              }
            />
          )}
        </DataGridStyles>
      </Container>

      {course && (
        <PopUpMenu
          anchorEl            = {anchorEl}
          open                = {Boolean(anchorEl && course)}
          TransitionComponent = {Fade}
          sx                  = {{ top: '-0.5rem', left: '-2rem' }}
          onClose             = {() => {
            setAnchorEl(null);
            setOpenConfirmPopup(false);
          }}
        >
          <MenuItem
            onClick={() => routingStore.push(Pages.Editor + `/${course.id}` as Pages)}
          >
            {t('edit')}
          </MenuItem>
          <MenuItem
            onClick = {async () => {
              const newCourse: Course = await course.clone();
              if (newCourse) {
                setCourses([...courses, newCourse]);
                setAnchorEl(null);
                toast(t('toast_success_duplicate_course'), 'success');
              } else {
                toast('toast_error_duplicate_course', 'error');
              }
            }}
          >
            {t('duplicate')}
          </MenuItem>
          <ConfirmPopup
            placement = 'left-end'
            onClose   = {() => setOpenConfirmPopup(false)}
            open      = {openConfirmPopup}
            onConfirm = {() => {
              setCourses(courses.filter((c) => c.id !== course.id));
              course.remove();
              setAnchorEl(null);
              setOpenConfirmPopup(false);
            }}
            onCancel = {() => {
              setOpenConfirmPopup(false);
              setAnchorEl(null);
            }}
          >
            <MenuItem onClick={() => setOpenConfirmPopup(true)}>
              {t('delete')}
            </MenuItem>
          </ConfirmPopup>
        </PopUpMenu>
      )}
      <Modal
        open    = {openConfirmModal}
        onClose = {() => setOpenConfirmModal(false)}
      >
        <Box
          display        = 'flex'
          flexDirection  = 'column'
          justifyContent = 'center'
          alignItems     = 'center'
          p              = {3}
        >
          <Typography variant='h3' mb={2}>
            {t('course_confirmation_title')}
          </Typography>
          <Typography variant='body1' mb={3}>
            {t('course_confirmation_body')}
          </Typography>
          <FormControl sx={{ marginBottom: 3 }}>
            <TextField
              hiddenLabel
              value    = {athenId}
              variant  = 'outlined'
              onChange = {(event) => {
                setAthenId(event.target.value)
              }}
            />
          </FormControl>
          <Button
            variant = 'contained'
            onClick = {async () => {
              const new_course: Course = Course.create({
                status: DataStatus.Draft,
                title:  athenId,
                desc:   ' ',
              });
              let success = true;
              success = success && Boolean(await new_course.save());
              success = success && Boolean(await new_course.loadSteps());
              
              // Create two default steps for the new course, an introduciton and the final quiz
              success = success && Boolean(await new_course.addChapter(StepType.Read, {
                title: t("introduction"),
                name:  t("introduction")
              }));
              success = success && Boolean(await new_course.addChapter(StepType.Quiz, {
                title: t("exam"),
                name:  t("exam"),
                mandatory: true,
              }));

              if(!success) {
                console.error('Something went wrong');
                toast(t('toast_error_create_course'), 'error');
                return;
              }
              
              routingStore.push(Pages.Editor, {id: '' + new_course.id, subview: 'settings'});
            }}
          >
            {t('create_course')}
          </Button>
        </Box>
      </Modal>
    </>
  );
}));

const PopUpMenu = styled(Menu)`
  .MuiList-root {
    padding-top:    0;
    padding-bottom: 0;
  }

  .MuiMenuItem-root {
    :not(:last-child) {
      border-bottom: 2px solid #f4f4f0;
    }
    padding: 0.5rem 1rem;
  }
`;
